"use client"

import { useState, useEffect, useCallback, useRef } from 'react'

// Game constants
const BOARD_SIZE = 20
const SNAKE_START = [
  { x: 10, y: 10 },
  { x: 10, y: 11 },
]
const APPLE_START = { x: 5, y: 5 }
const SCALE = 25  // Increased from 20 to 25 to match Tetris
const SPEED = 100
const DIRECTIONS = {
  38: { x: 0, y: -1 }, // up
  40: { x: 0, y: 1 }, // down
  37: { x: -1, y: 0 }, // left
  39: { x: 1, y: 0 }, // right
}

// Get random starting direction
const getRandomDirection = () => {
  const directionKeys = [37, 38, 39, 40] // left, up, right, down
  const randomKey = directionKeys[Math.floor(Math.random() * directionKeys.length)]
  return DIRECTIONS[randomKey]
}

// Utility functions
const checkCollision = (piece, snakeArray) =>
  snakeArray.some((segment) => segment.x === piece.x && segment.y === piece.y)

const appleAte = (newSnake, apple) =>
  newSnake[0].x === apple.x && newSnake[0].y === apple.y

const generateApple = (snake) => {
  let newApple
  do {
    newApple = {
      x: Math.floor(Math.random() * BOARD_SIZE),
      y: Math.floor(Math.random() * BOARD_SIZE),
    }
  } while (checkCollision(newApple, snake))
  return newApple
}

const getRandomColor = () => {
  // Vibrant neon colors matching Pink Pixel OS theme
  const neonColors = [
    "#ff2b6e", // neon-red
    "#39ff14", // neon-green
    "#00e1ff", // neon-blue
    "#bf00ff", // neon-purple
    "#ff00ff", // neon-pink
    "#ffff00", // neon-yellow
    "#ff9900", // neon-orange
  ]
  return neonColors[Math.floor(Math.random() * neonColors.length)]
}

const getSnakeColor = (index, isRainbow, rainbowColors) => {
  if (isRainbow) {
    return rainbowColors[index % rainbowColors.length]
  }
  return index === 0 ? "#39ff14" : "#33cc14" // neon-green and slightly darker
}

// Board component
const Board = ({ snakeDots, apple, isRainbowMode, rainbowColors }) => {
  return (
    <div
      className="grid bg-black/80 relative z-10 border border-gray-600"
      style={{
        gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
        width: `${BOARD_SIZE * SCALE}px`,
        height: `${BOARD_SIZE * SCALE}px`,
      }}
    >
      {Array.from({ length: BOARD_SIZE * BOARD_SIZE }).map((_, index) => {
        const x = index % BOARD_SIZE
        const y = Math.floor(index / BOARD_SIZE)

        // Check if this cell is part of the snake
        const snakeIndex = snakeDots.findIndex((dot) => dot.x === x && dot.y === y)
        const isSnake = snakeIndex !== -1
        const isSnakeHead = snakeIndex === 0

        // Check if this cell is the apple
        const isApple = apple.x === x && apple.y === y

        return (
          <div
            key={`${y}-${x}`}
            className="transition-all duration-200 border border-gray-700/50"
            style={{
              width: `${SCALE}px`,
              height: `${SCALE}px`,
              backgroundColor: isSnake
                ? getSnakeColor(snakeIndex, isRainbowMode, rainbowColors)
                : isApple
                ? "#ff2b6e"
                : "rgba(30, 30, 40, 0.8)",
              transform: isApple ? "scale(1.1)" : isSnakeHead ? "scale(1.05)" : "scale(1)",
              boxShadow: isSnake
                ? `0 0 8px ${getSnakeColor(snakeIndex, isRainbowMode, rainbowColors)}`
                : isApple
                ? "0 0 10px #ff2b6e"
                : "none",
              borderRadius: isSnakeHead ? "4px" : isSnake ? "2px" : "0px",
              animation: isApple ? "pulse 0.8s ease-in-out infinite alternate" : "none",
            }}
          />
        )
      })}
    </div>
  )
}

export default function SnakeApp() {
  const [snake, setSnake] = useState(SNAKE_START)
  const [apple, setApple] = useState(APPLE_START)
  const [dir, setDir] = useState(DIRECTIONS[39])
  const [speed, setSpeed] = useState(null)
  const [gameOver, setGameOver] = useState(false)
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [level, setLevel] = useState(1)
  const [isRainbowMode, setIsRainbowMode] = useState(false)
  const [rainbowColors, setRainbowColors] = useState([])
  const [appleEaten, setAppleEaten] = useState(false)

  const moveSnake = useCallback(() => {
    const newSnake = [...snake]
    const newSnakeHead = { x: newSnake[0].x + dir.x, y: newSnake[0].y + dir.y }

    if (
      newSnakeHead.x < 0 ||
      newSnakeHead.x >= BOARD_SIZE ||
      newSnakeHead.y < 0 ||
      newSnakeHead.y >= BOARD_SIZE ||
      checkCollision(newSnakeHead, snake)
    ) {
      setGameOver(true)
      return
    }

    newSnake.unshift(newSnakeHead)
    if (appleAte(newSnake, apple)) {
      setApple(generateApple(newSnake))
      setScore((prevScore) => {
        const newScore = prevScore + 1
        // Level up every 5 points
        if (newScore % 5 === 0) {
          setLevel((prevLevel) => prevLevel + 1)
          setSpeed((prevSpeed) => (prevSpeed ? prevSpeed * 0.9 : SPEED * 0.9))
        }
        return newScore
      })
      setAppleEaten(true)
      setTimeout(() => setAppleEaten(false), 300)
    } else {
      newSnake.pop()
    }
    setSnake(newSnake)
  }, [snake, dir, apple])

  const startGame = useCallback(() => {
    setSnake(SNAKE_START)
    setApple(APPLE_START)
    setDir(getRandomDirection()) // Random starting direction
    setSpeed(SPEED)
    setGameOver(false)
    setScore(0)
    setLevel(1)
    setRainbowColors(Array(SNAKE_START.length).fill("").map(getRandomColor))
  }, [])

  const toggleRainbow = () => {
    setIsRainbowMode((prev) => !prev)
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.keyCode
      if (key >= 37 && key <= 40) {
        // Prevent default scrolling behavior
        e.preventDefault()
        
        // Only allow direction change if game is running
        if (!gameOver && speed) {
          const newDir = DIRECTIONS[key]
          
          // Prevent the snake from reversing into itself
          const isOppositeDirection = 
            (dir.x === 1 && newDir.x === -1) ||  // right to left
            (dir.x === -1 && newDir.x === 1) ||  // left to right
            (dir.y === 1 && newDir.y === -1) ||  // down to up
            (dir.y === -1 && newDir.y === 1)     // up to down
          
          if (!isOppositeDirection) {
            setDir(newDir)
          }
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [dir, gameOver, speed])

  useEffect(() => {
    if (gameOver) {
      setSpeed(null)
      setHighScore((prev) => Math.max(prev, score))
    }
  }, [gameOver, score])

  useEffect(() => {
    if (!gameOver && speed) {
      const interval = setInterval(moveSnake, speed)
      return () => clearInterval(interval)
    }
  }, [moveSnake, gameOver, speed])

  useEffect(() => {
    if (isRainbowMode) {
      const interval = setInterval(() => {
        setRainbowColors((prev) => {
          const newColors = [...prev]
          newColors.pop()
          newColors.unshift(getRandomColor())
          return newColors
        })
      }, 200)
      return () => clearInterval(interval)
    }
  }, [isRainbowMode])

  return (
    <div className="flex flex-col items-center h-full p-4 bg-gray-900 text-white overflow-y-auto select-none">
      <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-green-400">
        🐍 Snake Game
      </h1>
      
      <div className="flex flex-col lg:flex-row items-start gap-6 pb-4">
        {/* Game Board */}
        <div className="bg-black/70 p-4 rounded-xl shadow-xl border border-green-500/30">
          <div className="relative">
            <Board snakeDots={snake} apple={apple} isRainbowMode={isRainbowMode} rainbowColors={rainbowColors} />
          </div>
        </div>

        {/* Game Info & Controls */}
        <div className="flex flex-col gap-4 min-w-[200px]">
          <div className="bg-black/50 p-4 rounded-xl border border-green-500/30">
            <div className="text-center">
              <div className="text-xl font-bold text-green-400 mb-2">
                Score: {score}
              </div>
              <div className="text-lg text-blue-400 mb-2">
                Level: {level}
              </div>
              <div className="text-sm text-purple-400 mb-4">
                High Score: {highScore}
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={startGame}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors border border-green-400"
                >
                  {gameOver ? '🔄 Play Again' : '🎮 Start Game'}
                </button>
                
                <button
                  onClick={toggleRainbow}
                  className={`w-full px-4 py-2 font-bold rounded-lg transition-colors border ${
                    isRainbowMode 
                      ? 'bg-purple-600 hover:bg-purple-700 border-purple-400 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 border-gray-500 text-gray-300'
                  }`}
                >
                  {isRainbowMode ? '🌈 Rainbow ON' : '🌈 Rainbow OFF'}
                </button>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-black/50 p-4 rounded-xl border border-green-500/30">
            <h3 className="text-base font-bold text-pink-400 mb-2">
              🎮 Controls
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <div>⬅️ Move Left</div>
              <div>➡️ Move Right</div>
              <div>⬆️ Move Up</div>
              <div>⬇️ Move Down</div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-black/50 p-4 rounded-xl border border-green-500/30">
            <h3 className="text-base font-bold text-blue-400 mb-2">
              ✨ Features
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <div>🐍 Growing Snake</div>
              <div>🌈 Rainbow Mode</div>
              <div>💫 Smooth Animations</div>
              <div>⚡ Progressive Speed</div>
              <div>🏆 High Score Tracking</div>
            </div>
          </div>
        </div>
      </div>

      {/* Game Over Modal */}
      {gameOver && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 p-6 sm:p-8 rounded-xl shadow-2xl border border-green-500/50 max-w-md w-full text-center">
            <h2 className="text-3xl font-bold mb-4 text-green-400">
              🐍 Game Over!
            </h2>
            <p className="text-xl mb-4 text-white">
              Final Score: <span className="text-green-400 font-bold">{score}</span>
            </p>
            <p className="text-base mb-2 text-gray-300">
              Level Reached: <span className="text-blue-400 font-bold">{level}</span>
            </p>
            <p className="text-base mb-6 text-gray-300">
              High Score: <span className="text-purple-400 font-bold">{highScore}</span>
            </p>
            <button
              onClick={startGame}
              className="w-full px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors border border-green-400"
            >
              🚀 Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
